import { useFormik } from "formik";
import * as Yup from "yup";
import { Button } from "../../../../components/buttons/button";
import { FileUploadInput } from "../../../../components/inputs/fileUploadInput";
import { TextInput } from "../../../../components/inputs/textInput";
import { getBase64 } from "../../../../helperFunctions";
import {
  JoinCooperativePayload,
  SingleCooperativeDetails,
} from "../../../../redux/features/cooperative/cooperative.type";

const spaceDelimiter = "_____";

export const MembershipForm = ({
  cooperativeDetails,
  onProceed,
  onPrevious,
  loading,
}: {
  cooperativeDetails: SingleCooperativeDetails;
  onProceed: (val: JoinCooperativePayload) => void;
  onPrevious: () => void;
  loading: boolean;
}) => {
  const formDetails = cooperativeDetails?.formdetail;
  const fields = formDetails?.Field;

  let initialValues: Record<string, any> = {};
  let schemaFields: Record<string, any> = {};

  for (let i = 0; i < fields.length; i++) {
    const currField = fields.at(i)!;

    const objectKey = currField.question.replaceAll(" ", spaceDelimiter);

    if (currField.fieldType === "file") {
      // Initial value: [fileList, preview]
      initialValues[objectKey] = [[], ""];

      // Custom Yup validation for file input (adjust as needed)
      schemaFields[objectKey] = Yup.array()
        .min(1, "At least one file is required")
        .required(`${currField.question} is required`);
    } else {
      // Initial value: empty string
      initialValues[objectKey] = "";

      // Yup string validation
      schemaFields[objectKey] = Yup.string()
        .trim()
        .required(`${currField.question} is required`);
    }
  }

  const schema = Yup.object().shape(schemaFields);

  const {
    values,
    handleChange,
    setFieldValue,
    handleBlur,
    touched,
    errors,
    handleSubmit,
  } = useFormik({
    initialValues: initialValues,
    validationSchema: schema,
    onSubmit: (data) => {
      const fieldAnswers = Object.keys(data).map((k) => {
        const singleField = fields?.find(
          (field) => field?.question === k?.replaceAll(spaceDelimiter, " ")
        )!;

        return {
          id: singleField.id,
          answer:
            singleField.fieldType === "file"
              ? {
                  fileType: data[k][0][0]?.name?.split(".")[1],
                  fileName: data[k][0][0]?.name?.split(".")[0],
                  fileBase64: data[k][1],
                }
              : data[k],
        };
      });

      onProceed({
        coop: cooperativeDetails.id,
        form: cooperativeDetails.formdetail.form.id,
        formdata: fieldAnswers,
      });
    },
  });

  return (
    <form
      className="w-full flex flex-1 h-fit min-h-full flex-col gap-4"
      onSubmit={handleSubmit}
    >
      {fields?.map((field, idx) => {
        const key = `formField-${idx}`;
        const fieldName = field?.question?.replaceAll(" ", spaceDelimiter);

        if (field?.fieldType === "file") {
          return (
            <div key={key}>
              <p className={`text-sm text-pry-dark-green-300 font-medium mb-1`}>
                {field.question}
              </p>

              <FileUploadInput
                title={`${field?.question}`}
                maxNumberOfFiles={1}
                selectedFiles={
                  values?.[fieldName] && values?.[fieldName]?.length > 0
                    ? values?.[fieldName][0]
                    : ""
                }
                handleChange={(files) => {
                  getBase64(files[0]).then((resp) => {
                    setFieldValue(fieldName, [files, resp]);
                  });
                }}
                errors={
                  errors?.[fieldName] && touched?.[fieldName]
                    ? (errors?.[fieldName] as string)
                    : ""
                }
                types="a file"
              />
            </div>
          );
        } else {
          return (
            <TextInput
              type={field?.fieldType}
              name={fieldName}
              value={values?.[fieldName]}
              placeholder={field?.question}
              onChange={handleChange}
              onBlur={handleBlur}
              hasIcon={false}
              iconSvg={undefined}
              errors={
                errors?.[fieldName] && touched?.[fieldName]
                  ? (errors?.[fieldName] as string)
                  : ""
              }
              inputClass=""
              className=""
              containerClassName=""
              customIcon={undefined}
              hasTitle
              title={`${field?.question}`}
              key={key}
            />
          );
        }
      })}

      <div className="grid grid-cols-2 gap-3 mt-auto">
        <Button
          text="Previous"
          onClick={() => {
            onPrevious();
          }}
          type="button"
          className=""
          loading={false}
          disabled={loading}
          variation="secondary"
        />

        <Button
          text="Next"
          onClick={() => {}}
          type="submit"
          className=""
          loading={loading}
          disabled={loading}
          variation="primary"
        />
      </div>
    </form>
  );
};
